"use client";

import { useEffect, useState } from "react";
import type { SaveStatus } from "@workspace/collaboration/types";
import { resolveStatus, type StatusKind } from "@/lib/document-status";
import { cn } from "@workspace/ui/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { CloudUpload, CloudOff, type LucideProps } from "lucide-react";

/**
 * `CloudCheck` upstream, inlined: `apps/docs` is on lucide-react 0.460, which
 * predates that icon. The path data is lucide's own (ISC), so it is identical
 * to the real thing on the same 24px grid and stroke — kept local rather than
 * bumping a shared dependency for one glyph. Delete this in favour of the
 * import if lucide is ever upgraded past 0.5x.
 */
function CloudCheck({ className, ...props }: LucideProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      <path d="m17 15-5.5 5.5L9 18" />
      <path d="M5 17.743A7 7 0 1 1 15.71 10h1.79a4.5 4.5 0 0 1 1.5 8.742" />
    </svg>
  );
}

/**
 * Three display states, all in the inherited text colour — the icon carries the
 * meaning and nothing competes with the document for attention.
 *
 * The five underlying `StatusKind`s collapse into these: anything with work
 * still to persist reads as `saving`, and anything the connection has broken
 * reads as `offline`. The precise state survives in the tooltip wording, so
 * "offline" and "failed to save" stay distinguishable on inspection without
 * needing two icons for what the user does about them (wait).
 */
const DISPLAY = {
  saved: { Icon: CloudCheck, pulse: false },
  saving: { Icon: CloudUpload, pulse: true },
  offline: { Icon: CloudOff, pulse: false },
} as const;

const STATUS: Record<
  StatusKind,
  { label: string; display: keyof typeof DISPLAY }
> = {
  saved: { label: "Saved", display: "saved" },
  unsaved: { label: "Unsaved changes", display: "saving" },
  saving: { label: "Saving…", display: "saving" },
  offline: { label: "Offline — changes saved locally", display: "offline" },
  failed: { label: "Failed to save", display: "offline" },
};

/**
 * Combined connection + save indicator (replaces the separate "Connected" wifi
 * badge and "Saved" text). Icon-only in the title bar; the wording lives in the
 * tooltip, and in `aria-label` for anyone who cannot hover it.
 */
export function DocumentStatus({
  saveStatus,
  className,
}: {
  saveStatus: SaveStatus;
  className?: string;
}) {
  // Seeded lazily rather than in an effect: `navigator.onLine` is readable at
  // first client render, and this component is only ever rendered on the
  // client (the editor page is a client tree), so there is no hydration
  // mismatch to guard against.
  const [online, setOnline] = useState(() =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  );

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const { label, display } = STATUS[resolveStatus(saveStatus, online)];
  const { Icon, pulse } = DISPLAY[display];

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "flex items-center shrink-0 rounded-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
              className
            )}
            // Focusable so the tooltip is reachable without a pointer: with the
            // label gone, it is the only place the status is spelled out.
            tabIndex={0}
            role="status"
            aria-live="polite"
            aria-label={label}
          >
            <Icon
              className={cn("w-4 h-4 shrink-0 ", pulse && "animate-pulse")}
              // The wrapper supplies the accessible name; without this the
              // glyph shows up as a second, nameless `img` node inside it.
              aria-hidden="true"
            />
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
